import type { ReactNode } from "react";

import { usePageMeta } from "./hooks";
import "./PrivacyPage.css";

/* BORRADOR (2026-09-29): falta que Eduardo confirme los datos del responsable
 * y que lo revise un abogado. Mientras quede algún dato entre corchetes, la
 * página muestra el aviso amarillo «Borrador: pendiente de revisión legal». */
export const RESPONSABLE = "[RAZÓN SOCIAL POR CONFIRMAR]";
export const DOMICILIO = "[DOMICILIO POR CONFIRMAR]";
export const CORREO_PRIVACIDAD = "[CORREO DE PRIVACIDAD POR CONFIRMAR]";
export const FECHA_ACTUALIZACION = "29 de septiembre de 2026";

/** true si el valor sigue siendo un dato por confirmar ("[… POR CONFIRMAR]"). */
export function isPlaceholder(value: string): boolean {
  const text = value.trim();
  return text === "" || /por confirmar/i.test(text) || /^\[.*\]$/.test(text);
}

/** true mientras algún dato del responsable siga pendiente. */
export function privacyNoticeIsDraft(values: string[] = [RESPONSABLE, DOMICILIO, CORREO_PRIVACIDAD]): boolean {
  return values.some(isPlaceholder);
}

function Dato({ value }: { value: string }) {
  return isPlaceholder(value) ? <mark className="t123-privacy__todo">{value}</mark> : <>{value}</>;
}

function Correo() {
  if (isPlaceholder(CORREO_PRIVACIDAD)) return <Dato value={CORREO_PRIVACIDAD} />;
  return <a href={`mailto:${CORREO_PRIVACIDAD}`}>{CORREO_PRIVACIDAD}</a>;
}

interface NoticeSection {
  id: string;
  title: string;
  body: ReactNode;
}

const SECTIONS: NoticeSection[] = [
  {
    id: "responsable",
    title: "Quién es responsable de tus datos",
    body: (
      <>
        <p>
          <Dato value={RESPONSABLE} /> («Torneos123»), con domicilio en <Dato value={DOMICILIO} />, es responsable del
          tratamiento de los datos personales que recabamos a través del sitio torneos123.com y de la aplicación
          Torneos123.
        </p>
        <p>
          Para cualquier asunto relacionado con tus datos personales, escríbenos a <Correo />.
        </p>
        <p>
          Los torneos que se publican en Torneos123 los organizan clubes, academias, ligas y organizadores
          independientes (los «organizadores»). Cuando te inscribes a un torneo, su organizador también recibe tus
          datos para organizarlo; te lo explicamos en la sección 4.
        </p>
      </>
    ),
  },
  {
    id: "datos",
    title: "Qué datos personales recabamos",
    body: (
      <>
        <h3>Si creas tu cuenta o te inscribes a un torneo</h3>
        <ul>
          <li>
            <strong>Identificación:</strong> nombre y apellidos, fecha de nacimiento, género, nacionalidad, entidad y
            ciudad, CURP (y tu CURP en PDF, si la subes) y tu fotografía.
          </li>
          <li>
            <strong>Contacto:</strong> número de celular y correo electrónico.
          </li>
          <li>
            <strong>Datos deportivos:</strong> club, categorías, mano dominante, tipo de revés, número de federación o de
            WTN si los tienes, resultados, posiciones, ranking e historial de partidos.
          </li>
          <li>
            <strong>Datos de tu entrenador,</strong> si los das: nombre, celular y correo.
          </li>
          <li>Tus respuestas a las preguntas adicionales que el organizador agregue a su formulario de inscripción.</li>
          <li>Tu usuario y contraseña. La contraseña se guarda cifrada: nadie en Torneos123 puede leerla.</li>
        </ul>

        <h3>Datos de menores de edad</h3>
        <p>
          Los torneos infantiles y juveniles requieren datos de menores de edad. Esos datos deben darlos su madre, padre
          o tutor, o el menor con su autorización. El organizador de cada torneo puede pedir en su formulario de
          inscripción, además de los datos de la lista anterior, los datos del tutor (nombre, parentesco, celular y
          correo) y su consentimiento. Con una cuenta familiar, una mamá o un papá puede inscribir a varios hijos. Los
          datos de menores se usan para inscribirlos y organizar sus torneos, lo que incluye publicar sus resultados,
          sus posiciones en tablas y rankings y su credencial digital con fotografía, como se explica en la sección 3.
        </p>

        <h3>Si pagas una inscripción</h3>
        <p>
          Los pagos en línea los procesa Mercado Pago. El número completo de tu tarjeta y su código de seguridad los
          capturas directamente en Mercado Pago: Torneos123 nunca los recibe. Cuando pagas, Mercado Pago nos envía los
          datos del pago y los guardamos con el registro de tu pago: el monto, la fecha, el estado y el número de
          operación, además de otros datos que pueden incluir los primeros y los últimos dígitos de la tarjeta, su
          fecha de vencimiento, el nombre y la identificación del titular de la tarjeta, y el correo y la
          identificación de quien paga. Si el organizador acepta pagos por transferencia, también guardamos el comprobante que subas.
        </p>

        <h3>Si pides una demo</h3>
        <p>
          En el formulario «Pedir demo» recabamos tu nombre, celular, correo, ciudad y deporte y, si decides darlos, el
          tipo y nombre de tu organización, el tamaño y la fecha de tu próximo torneo y tu mensaje. También registramos
          la página desde la que nos escribiste y, si llegaste desde otro sitio o desde el enlace de una campaña, el
          nombre de ese sitio o de esa campaña. Tu dirección IP solo se usa en el momento, para evitar envíos abusivos,
          y no se guarda con tu solicitud.
        </p>

        <h3>Datos técnicos</h3>
        <p>
          Al usar el sitio, nuestros servidores registran datos técnicos como tu dirección IP, el tipo de navegador y
          la fecha y hora de acceso, para mantener la seguridad y el buen funcionamiento de la plataforma.
        </p>

        <h3>No pedimos datos sensibles</h3>
        <p>
          Torneos123 no te pide datos personales sensibles, como los de salud, origen étnico, creencias religiosas u
          orientación sexual. Te pedimos no incluirlos en tus mensajes ni en tus respuestas a las preguntas adicionales
          de un torneo.
        </p>

        <h3>Datos de otras personas</h3>
        <p>
          Si nos das datos de otra persona (por ejemplo, de tu hijo, de tu pareja de dobles o de un jugador al que
          inscribes el día del torneo), confirmas que tienes su autorización o que ejerces su patria potestad o tutela.
        </p>
      </>
    ),
  },
  {
    id: "finalidades",
    title: "Para qué usamos tus datos",
    body: (
      <>
        <h3>Finalidades necesarias</h3>
        <p>Usamos tus datos para lo siguiente, que es necesario para darte el servicio:</p>
        <ul>
          <li>Crear y administrar tu cuenta y, en su caso, tu cuenta familiar.</li>
          <li>
            Inscribirte a los torneos que elijas y validar que cumples los requisitos de cada categoría, como año de
            nacimiento y rama.
          </li>
          <li>Compartir tu inscripción con el organizador del torneo para que lo organice.</li>
          <li>Procesar y registrar tus pagos, enviarte recordatorios de pago y hacer reembolsos.</li>
          <li>
            Organizar el torneo: sorteos, grupos, cuadros, calendario de partidos, resultados, tablas de posiciones,
            rankings y tu credencial digital.
          </li>
          <li>
            Generar tu credencial digital del torneo, que muestra tu nombre completo, tu fotografía, tu fecha de
            nacimiento completa y tu edad, con un código QR para verificarla. Cualquier persona que escanee ese código,
            sin iniciar sesión, ve tu nombre, fotografía, fecha de nacimiento, edad y club para comprobar que la
            credencial es válida.
          </li>
          <li>
            Enviarte avisos del torneo por WhatsApp, por notificación en tu celular o por correo: recordatorios de
            partido, cambios de horario, confirmación de pago, sorteo y comunicados del organizador.
          </li>
          <li>
            Mostrar a los demás jugadores tu nombre, categoría, club, resultados y posiciones, y tu fotografía en tu
            credencial digital. Cuando el organizador publica su torneo o su ranking, esa información también puede
            verla cualquier persona.
          </li>
          <li>
            Si usas la función de amigos y partidos amistosos (retas): otros jugadores con cuenta pueden encontrar tu
            perfil buscando tu nombre o tu correo, y tus amigos ven tu ciudad y tu club.
          </li>
          <li>Atender tus dudas y solicitudes de ayuda.</li>
          <li>Si pediste una demo, contactarte por WhatsApp, llamada o correo para agendarla y darle seguimiento.</li>
          <li>Proteger la seguridad de la plataforma, prevenir fraudes y abusos y cumplir nuestras obligaciones legales.</li>
        </ul>

        <h3>Finalidades adicionales</h3>
        <p>Si no te opones, también usamos tus datos para:</p>
        <ul>
          <li>Enviarte información sobre torneos, funciones nuevas y promociones de Torneos123.</li>
          <li>Invitarte a contestar encuestas sobre tu experiencia con la plataforma.</li>
        </ul>
        <p>
          Estas finalidades no son necesarias para darte el servicio. Si no quieres que usemos tus datos para ellas,
          escríbenos a <Correo /> con el asunto «No a finalidades adicionales». Tu negativa no afecta tu cuenta ni tus
          inscripciones. Los datos del formulario «Pedir demo» nunca se usan para estas finalidades.
        </p>
      </>
    ),
  },
  {
    id: "transferencias",
    title: "Con quién compartimos tus datos",
    body: (
      <>
        <p>No vendemos ni rentamos tus datos personales. Solo los compartimos en estos casos:</p>
        <ul>
          <li>
            <strong>El organizador del torneo al que te inscribes,</strong> para organizarlo, validar tu inscripción y
            comunicarse contigo sobre el torneo. Para ayudarte con dudas o problemas, el organizador y su equipo pueden
            entrar a tu vista de la aplicación y ver lo mismo que tú, con acceso a las funciones de tu cuenta de jugador
            por hasta 30 minutos cada vez.
          </li>
          <li>
            <strong>Mercado Pago,</strong> que procesa el pago de tu inscripción. Mercado Pago también trata tus datos
            conforme a su propio aviso de privacidad.
          </li>
          <li>
            <strong>Proveedores que nos ayudan a operar la plataforma:</strong> envío de mensajes por WhatsApp, envío de
            correos, notificaciones al celular (por medio de los servicios de tu navegador o de tu teléfono) y
            servidores en la nube donde se guarda la información. Solo pueden usar tus datos para prestarnos su
            servicio. Algunos están fuera de México.
          </li>
          <li>
            <strong>Autoridades competentes,</strong> cuando la ley nos obligue o lo ordene una autoridad.
          </li>
        </ul>
        <p>
          Estas transferencias no requieren tu consentimiento porque son necesarias para cumplir la relación que tienes
          con nosotros o porque así lo establece la ley.
        </p>
      </>
    ),
  },
  {
    id: "derechos-arco",
    title: "Tus derechos ARCO y cómo ejercerlos",
    body: (
      <>
        <p>
          Tienes derecho a conocer qué datos tenemos de ti y para qué los usamos (<strong>acceso</strong>); a pedir
          que los corrijamos si están equivocados o incompletos (<strong>rectificación</strong>); a pedir que los
          eliminemos de nuestros registros y bases de datos (<strong>cancelación</strong>), y a oponerte a que los
          usemos para fines específicos (<strong>oposición</strong>). A estos se les conoce como derechos ARCO.
        </p>
        <p>
          Para ejercerlos, envía tu solicitud a <Correo /> con:
        </p>
        <ol>
          <li>Tu nombre y el correo o medio por el que quieres recibir la respuesta.</li>
          <li>
            Una copia de tu identificación oficial. Si actúas en nombre de otra persona o de un menor de edad, el
            documento que acredite que lo representas.
          </li>
          <li>La descripción clara de los datos y del derecho que quieres ejercer.</li>
          <li>Cualquier dato que nos ayude a encontrar tu información, por ejemplo, el torneo en el que te inscribiste.</li>
          <li>Si pides una corrección, el dato correcto y, en su caso, el documento que lo respalde.</li>
        </ol>
        <p>
          Te responderemos en un plazo máximo de 20 días hábiles a partir de que recibamos tu solicitud completa. Si
          procede, la haremos efectiva dentro de los 15 días hábiles siguientes a nuestra respuesta. Algunos datos
          también los puedes corregir tú desde tu perfil en la aplicación.
        </p>
        <p>
          En algunos casos no podremos eliminar todos tus datos, por ejemplo, cuando la ley nos obliga a conservarlos,
          como los registros de pagos.
        </p>
        <p>
          Si consideras que tu derecho a la protección de tus datos personales fue vulnerado, puedes acudir a la
          autoridad competente en materia de protección de datos personales.
        </p>
      </>
    ),
  },
  {
    id: "revocacion",
    title: "Revocar tu consentimiento o limitar el uso de tus datos",
    body: (
      <>
        <p>
          Puedes revocar en cualquier momento el consentimiento que nos diste para tratar tus datos. Envía tu solicitud
          a <Correo /> con los mismos requisitos de la sección 5; te responderemos en los mismos plazos. Si revocas tu
          consentimiento para una finalidad necesaria, es posible que ya no podamos darte el servicio, por ejemplo,
          mantener tu inscripción en un torneo.
        </p>
        <p>Para limitar el uso de tus datos:</p>
        <ul>
          <li>Puedes desactivar las notificaciones al celular desde la configuración de tu navegador o de tu teléfono.</li>
          <li>
            Para dejar de recibir avisos por WhatsApp o por correo, escríbenos a <Correo />.
          </li>
        </ul>
      </>
    ),
  },
  {
    id: "cookies",
    title: "Cookies y almacenamiento en tu navegador",
    body: (
      <>
        <p>Torneos123 no usa cookies de publicidad ni herramientas de rastreo de terceros.</p>
        <p>Sí usamos el almacenamiento de tu navegador para que la aplicación funcione:</p>
        <ul>
          <li>Mantener tu sesión iniciada.</li>
          <li>Recordar si prefieres el tema claro u oscuro y qué perfil de tu cuenta familiar estás usando.</li>
          <li>Recordar si ya cerraste la invitación para instalar la aplicación o para activar las notificaciones.</li>
          <li>Guardar una copia de la aplicación para que cargue más rápido y siga funcionando si falla tu conexión.</li>
          <li>
            Guardar en tu dispositivo una copia de la información que consultas en la aplicación y de los archivos que
            abres o subes en ella, como fotografías, CURP en PDF y comprobantes de pago. Esa copia se borra cuando
            cierras sesión.
          </li>
        </ul>
        <p>
          Si activas las notificaciones al celular, tu navegador crea una suscripción que guardamos en nuestros
          servidores, junto con la identificación de tu navegador (qué navegador y sistema usas), para poder
          enviártelas. Puedes borrar los datos guardados en tu dispositivo desde la configuración de tu navegador; si
          lo haces, tendrás que volver a iniciar sesión.
        </p>
      </>
    ),
  },
  {
    id: "conservacion",
    title: "Cuánto tiempo guardamos tus datos y cómo los protegemos",
    body: (
      <>
        <p>
          Guardamos tus datos mientras tengas una cuenta o mientras sean necesarios para las finalidades de este aviso;
          después, solo durante los plazos que exija la ley. Los datos de una solicitud de demo los guardamos mientras
          le damos seguimiento.
        </p>
        <p>
          Protegemos tus datos con medidas administrativas, técnicas y físicas, como conexiones cifradas y contraseñas
          guardadas cifradas.
        </p>
        <p>
          Los archivos que subes (tu fotografía, tu CURP en PDF y tus comprobantes de pago) se guardan en nuestros
          servidores con enlaces privados y difíciles de adivinar. Quien tenga el enlace de un archivo puede abrirlo,
          así que no compartas esos enlaces.
        </p>
      </>
    ),
  },
  {
    id: "cambios",
    title: "Cambios a este aviso",
    body: (
      <p>
        Podemos modificar este aviso por cambios en la ley, en nuestros servicios o en nuestra forma de trabajar.
        Cualquier cambio lo publicaremos en esta página, torneos123.com/aviso-de-privacidad, con su nueva fecha de
        actualización.
      </p>
    ),
  },
];

/** /aviso-de-privacidad: aviso de privacidad integral (borrador pendiente de
 * revisión legal). Cubre la inscripción a torneos, los datos de menores, los
 * pagos y el formulario «Pedir demo». */
export function PrivacyPage() {
  usePageMeta("privacy");
  const isDraft = privacyNoticeIsDraft();

  return (
    <main className="t123-privacy">
      {isDraft ? (
        <div className="t123-privacy__draft" role="note">
          <div className="t123-shell t123-privacy__draft-inner">
            <strong>
              <svg viewBox="0 0 24 24" width="1.15em" height="1.15em" aria-hidden="true" focusable="false">
                <path
                  d="M10.3 3.9 1.8 18a2 2 0 0 0 1.7 3h17a2 2 0 0 0 1.7-3L13.7 3.9a2 2 0 0 0-3.4 0ZM12 9v4M12 17h.01"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              Borrador: pendiente de revisión legal
            </strong>
            <span>Los datos marcados en amarillo están por confirmar.</span>
          </div>
        </div>
      ) : null}

      <header className="t123-subhero t123-privacy__hero">
        <div className="t123-shell">
          <span className="t123-kicker">Legal</span>
          <h1>Aviso de privacidad</h1>
          <p>
            En Torneos123 cuidamos tus datos personales. En este aviso te explicamos qué datos recabamos, para qué los
            usamos, con quién los compartimos y cómo puedes ejercer tus derechos, de acuerdo con la Ley Federal de
            Protección de Datos Personales en Posesión de los Particulares.
          </p>
          <p className="t123-privacy__updated">Última actualización: {FECHA_ACTUALIZACION}</p>
        </div>
      </header>

      <div className="t123-shell t123-privacy__body">
        <article className="t123-privacy__article">
          <nav className="t123-privacy__toc" aria-labelledby="t123-privacy-toc-title">
            <h2 id="t123-privacy-toc-title">En este aviso</h2>
            <ol>
              {SECTIONS.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ol>
          </nav>

          {SECTIONS.map((section, index) => (
            <section
              key={section.id}
              id={section.id}
              className="t123-privacy__section"
              aria-labelledby={`${section.id}-titulo`}
            >
              <h2 id={`${section.id}-titulo`}>
                <span className="t123-privacy__num">{index + 1}</span>
                {section.title}
              </h2>
              {section.body}
            </section>
          ))}

          <p className="t123-privacy__footnote">Última actualización: {FECHA_ACTUALIZACION}.</p>
        </article>
      </div>
    </main>
  );
}
